import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Heart, Activity, Thermometer, Wind, MessageSquare } from 'lucide-react';
import type { ReplitVitalSign } from '@/types/replit-dashboard';
import { ClinicalDataLanguageNotice } from '@/components/ClinicalDataLanguageNotice';

interface VitalSignsChartProps {
  vitalSigns: ReplitVitalSign[];
  onAskQuestion?: (question: string) => void;
  /** Hide the "Explain all vital signs" button when the enclosing page already has one. */
  showExplainAll?: boolean;
}

const VitalSignsChart: React.FC<VitalSignsChartProps> = ({ vitalSigns, onAskQuestion, showExplainAll = true }) => {
  const { t } = useTranslation();
  // Group vitals by type
  const groupedVitals = vitalSigns.reduce((acc, vital) => {
    if (!acc[vital.type]) {
      acc[vital.type] = [];
    }
    acc[vital.type].push(vital);
    return acc;
  }, {} as Record<string, ReplitVitalSign[]>);

  // Sort each group by date descending (most recent first)
  Object.values(groupedVitals).forEach(list => {
    list.sort((a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime());
  });

  const getIcon = (type: string) => {
    const lowerType = type.toLowerCase();
    if (lowerType.includes('heart') || lowerType.includes('pulse')) {
      return <Heart className="h-5 w-5 text-red-500" />;
    }
    if (lowerType.includes('blood pressure')) {
      return <Activity className="h-5 w-5 text-blue-500" />;
    }
    if (lowerType.includes('temperature')) {
      return <Thermometer className="h-5 w-5 text-orange-500" />;
    }
    if (lowerType.includes('respiratory') || lowerType.includes('oxygen')) {
      return <Wind className="h-5 w-5 text-green-500" />;
    }
    return <Activity className="h-5 w-5 text-gray-500" />;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-3">
      <ClinicalDataLanguageNotice />
      {onAskQuestion && showExplainAll && vitalSigns.length > 0 && (
        <button
          onClick={() => onAskQuestion(t('explain.vitalsAll'))}
          className="bg-white border border-gray-300 hover:border-primary text-primary hover:bg-primary hover:text-white px-2.5 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-all shadow-sm mb-2 w-full"
          data-testid="button-explain-vital-signs"
          aria-label={t('explain.vitalsAllAriaLabel')}
        >
          <MessageSquare className="h-3 w-3" />
          {t('explain.vitalsAllButtonLabel')}
        </button>
      )}

      <div className="grid grid-cols-1 gap-3">
        {Object.entries(groupedVitals).map(([type, vitalsList]) => (
          <Card key={type} className="p-3">
            <div className="flex items-center space-x-2 mb-2">
              {getIcon(type)}
              <h3 className="font-medium text-xs">{type}</h3>
            </div>

            <div className="space-y-2 max-h-32 overflow-y-auto">
              {vitalsList.map((vital, index) => (
                <div key={index} className="flex justify-between items-center py-1 text-[10px] border-b last:border-0">
                  <span className="text-gray-600">
                    {formatDate(vital.recordedAt)}
                  </span>
                  <span className="font-medium">
                    {vital.value} {vital.unit}
                  </span>
                </div>
              ))}
            </div>

            {onAskQuestion && (
              <div className="flex justify-end mt-2">
                <button
                  onClick={() => onAskQuestion(t('explain.vitalType', { type }))}
                  className="bg-white border border-gray-300 hover:border-primary text-primary hover:bg-primary hover:text-white px-2 py-1 rounded-md text-[9px] font-medium flex items-center gap-1 transition-all shadow-sm"
                  data-testid={`button-explain-${type.toLowerCase().replace(/\s+/g, '-')}`}
                  aria-label={t('explain.vitalTypeAriaLabel', { type })}
                >
                  <MessageSquare className="h-2.5 w-2.5" />
                  {t('explain.buttonLabel')}
                </button>
              </div>
            )}
          </Card>
        ))}
      </div>

      {Object.keys(groupedVitals).length === 0 && (
        <Card className="p-4 text-center text-gray-500 text-xs">
          {t('records.noVitalSignsDataAvailable')}
        </Card>
      )}
    </div>
  );
};

export default VitalSignsChart;
